import { useState } from 'react';
import { Button, Card, Form, Row, Col, Alert } from 'react-bootstrap';
import { FaUser, FaEnvelope, FaPhone, FaFileAlt } from 'react-icons/fa';
import { useNavigate, Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import 'libphonenumber-js/max';

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;


const stripePromise = loadStripe(
  'pk_live_51RqSTzFskmFtcwUcRbKVOv8mGRLhI6Ueh1B3vJQYKbFA3Wso7LvsZNLU6OAMCRyyAw4YT4D1gup70F0DfJ9oMoOV00tgamGMaL'
);

interface Props {
  appointmentData: {
    service: string;
    location: string;
    advisor: string;
    date: string;
    timeSlot: string;
    price: number;
    name: string;
    email: string;
    phone: string;
    note: string;
    termsAccepted: boolean;
  };
  onBack: () => void;
  onReserve: (data: {
    name: string;
    email: string;
    phone: string;
    note: string;
    termsAccepted: boolean;
  }) => void;
}

const UserInformationForm = ({ appointmentData, onBack, onReserve }: Props) => {
  const [name, setName] = useState(appointmentData.name);
  const [email, setEmail] = useState(appointmentData.email);
  const [phone, setPhone] = useState<string>(appointmentData.phone || '');
  const [note, setNote] = useState(appointmentData.note);
  const [acceptedTerms, setAcceptedTerms] = useState(
    appointmentData.termsAccepted
  );

  // State for individual field errors
  const [nameError, setNameError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [termsError, setTermsError] = useState<string | null>(null);
  const [generalError, setGeneralError] = useState<string | null>(null);

  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const serviceLabels: Record<string, string> = {
    corporate: 'Corporate Legal Solution & Compliance',
    resolution: 'Litigation & Dispute Resolution',
    Legal_Service: 'Land Revenue Litigation & Real Estate Legal Service',
    LPO_Service: 'High-Volume Legal Drafting & LPO Service',
  };

  const validateForm = () => {
    let isValid = true;

    // Validate Name
    if (!name.trim()) {
      setNameError('Full Name is required.');
      isValid = false;
    } else {
      setNameError(null);
    }

    // Validate Email (general format)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      setEmailError('Email Address is required.');
      isValid = false;
    } else if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address.');
      isValid = false;
    } else {
      setEmailError(null);
    }

    // Validate Phone Number
    // The phone number validation here seems a bit loose. react-international-phone typically handles this well.
    // For a more robust check, you might integrate a library like `libphonenumber-js`
    // within your validation logic (though `react-international-phone` uses it internally).
    if (
      !phone ||
      !/^\+\d{7,15}$/.test(phone) // A more standard regex for international numbers (7 to 15 digits)
    ) {
      setPhoneError('Please enter a valid phone number.');
      isValid = false;
    } else {
      setPhoneError(null);
    }

    // Validate Note
    if (!note.trim()) {
      setNoteError('Booking Note cannot be empty.');
      isValid = false;
    } else {
      setNoteError(null);
    }

    // Validate Terms and Conditions
    if (!acceptedTerms) {
      setTermsError('You must accept the terms and conditions.');
      isValid = false;
    } else {
      setTermsError(null);
    }

    if (!isValid) {
      setGeneralError('Please correct the errors in the form.');
    } else {
      setGeneralError(null);
    }

    return isValid;
  };

  const handleReserve = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setGeneralError(null);

    const reservationData = {
      service: appointmentData.service,
      location: appointmentData.location,
      advisor: appointmentData.advisor,
      date: appointmentData.date,
      timeSlot: appointmentData.timeSlot,
      price: appointmentData.price,
      name,
      email,
      phone,
      note,
      termsAccepted: acceptedTerms,
    };

    try {
      console.log('Creating checkout session with data:', reservationData);
      const response = await fetch(
        `${API_BASE_URL}/sendappointment/create-checkout-session`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(reservationData),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create checkout session: ${errorText}`);
      }

      const { sessionId } = await response.json();
      console.log('Session ID received:', sessionId);
      const stripe = await stripePromise;

      if (!stripe) {
        throw new Error('Stripe failed to initialize');
      }

      const { error: stripeError } = await stripe.redirectToCheckout({
        sessionId,
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      onReserve({ name, email, phone, note, termsAccepted: acceptedTerms });
    } catch (err: any) {
      console.error('Error in handleReserve:', err);
      setGeneralError(
        err.message ||
          'An error occurred while initiating payment. Please try again.'
      );
      setLoading(false);
    }
  };

  return (
    <div>
      {' '}
      {/* Adjusted padding for better responsiveness */}
      <div className="row justify-content-center">
        <div className="col-12 col-md-10 col-lg-8">
          {' '}
          {/* Wider on medium, full width on small */}
          <Card className="appointment-border rounded-0 mb-4 mb-md-5">
            {' '}
            {/* Added margin bottom */}
            <Card.Header className="bg-blue text-light rounded-0 py-3 py-md-4 border-0">
              <p className="mb-0  fw-light letter-spacing-1 fw-semibold text-center ">
                <span className="responsive-text">
                  Your Information
                </span>
              </p>
            </Card.Header>
            <Card.Body className="p-4 p-md-5">
              {' '}
              {/* Adjusted padding */}
              <Form>
                {generalError && (
                  <Alert variant="danger" className="mb-4">
                    {generalError}
                  </Alert>
                )}

                <Form.Group className="mb-4 mb-md-5">
                  {' '}
                  {/* Adjusted margin bottom */}
                  <Form.Label className="d-block text-uppercase small fw-bold text-muted mb-2 mb-md-3">
                    {' '}
                    {/* Adjusted margin bottom */}
                    Full Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      setNameError(null);
                    }}
                    placeholder="Enter your full name"
                    className={`py-2 py-md-3 px-3 px-md-4 border-top-0 border-start-0 border-end-0 rounded-0 border-2 bg-light ${nameError ? 'border-danger' : 'border-dark'}`}
                  />
                  {nameError && (
                    <Form.Text className="text-danger">{nameError}</Form.Text>
                  )}
                </Form.Group>

                <Form.Group className="mb-4 mb-md-5">
                  <Form.Label className="d-block text-uppercase small fw-bold text-muted mb-2 mb-md-3">
                    Email Address
                  </Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError(null);
                    }}
                    placeholder="Enter your email"
                    className={`py-2 py-md-3 px-3 px-md-4 border-top-0 border-start-0 border-end-0 rounded-0 border-2 bg-light ${emailError ? 'border-danger' : 'border-dark'}`}
                  />
                  {emailError && (
                    <Form.Text className="text-danger">{emailError}</Form.Text>
                  )}
                </Form.Group>

                <Form.Group className="mb-4 mb-md-5">
                  <Form.Label className="d-block text-uppercase small fw-bold text-muted mb-2 mb-md-3">
                    Phone Number
                  </Form.Label>
                  <PhoneInput
                    defaultCountry="ae"
                    value={phone}
                    onChange={(value) => {
                      setPhone(value);
                      setPhoneError(null);
                    }}
                    placeholder="Enter phone number"
                    className={`phone-input-custom ${phoneError ? 'is-invalid' : ''}`} // Custom class for styling
                  />
                  {phoneError && (
                    <Form.Text className="text-danger">{phoneError}</Form.Text>
                  )}
                </Form.Group>

                <Form.Group className="mb-4 mb-md-5">
                  <Form.Label className="d-block text-uppercase small fw-bold text-muted mb-2 mb-md-3">
                    Booking Note
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={note}
                    onChange={(e) => {
                      setNote(e.target.value);
                      setNoteError(null);
                    }}
                    placeholder="Write a note "
                    className={`py-2 py-md-3 px-3 px-md-4 border-top-0 border-start-0 border-end-0 rounded-0 border-2 bg-light ${noteError ? 'border-danger' : 'border-dark'}`}
                  />
                  {noteError && (
                    <Form.Text className="text-danger">{noteError}</Form.Text>
                  )}
                </Form.Group>

                <Card className="mb-4 mb-md-5 border-0 shadow-sm rounded-0">
                  <Card.Header className="bg-light rounded-0 py-3 border-0">
                    <h5 className="mb-0 letter-spacing-1 responsive-text-card-header">
                      Your Appointment Details
                    </h5>
                  </Card.Header>
                  <Card.Body className="p-3 p-md-4">
                    <Row className="mb-2">
                      <Col
                        xs={12}
                        sm={4} // Stack on extra small, side-by-side on small and up
                        className="text-uppercase small fw-bold text-muted mb-1 mb-sm-0"
                      >
                        Service
                      </Col>
                      <Col xs={12} sm={8}>
                        {serviceLabels[appointmentData.service] ||
                          appointmentData.service}
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        xs={12}
                        sm={4}
                        className="text-uppercase small fw-bold text-muted mb-1 mb-sm-0"
                      >
                        Total
                      </Col>
                      <Col xs={12} sm={8}>
                        AED {appointmentData.price}
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                <Form.Group className="mb-4 mb-md-5">
                  <Form.Check
                    type="checkbox"
                    id="terms"
                    label={
                      <>
                        accept the{' '}
                        <Link
                          to="/terms-conditions"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary"
                        >
                          terms & conditions
                        </Link>
                      </>
                    }
                    checked={acceptedTerms}
                    onChange={(e) => {
                      setAcceptedTerms(e.target.checked);
                      setTermsError(null);
                    }}
                    className={`text-muted ${termsError ? 'is-invalid' : ''}`}
                  />
                  {termsError && (
                    <Form.Text className="text-danger">{termsError}</Form.Text>
                  )}
                </Form.Group>

                <div className="d-flex flex-column flex-md-row justify-content-end mt-4 mt-md-5 pt-3 border-top border-dark">
                  <Button
                    onClick={onBack}
                    className="rounded-2 fw-semibold letter-spacing-1 btn-outline-dark me-md-3 mb-3 mb-md-0" // Adjusted margin and added margin-bottom for stacking
                    style={{
                      minWidth: '120px',
                      minHeight: '45px',
                      outline: 'none',
                      boxShadow: 'none',
                      border: '1px solid #082341',
                      background: 'transparent',
                      color: '#082341',
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.outline = 'none';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    Back
                  </Button>

                  <Button
                    onClick={handleReserve}
                    disabled={loading}
                    className="rounded-2 fw-semibold letter-spacing-1 btn-dark"
                    style={{
                      minWidth: '220px',
                      minHeight: '45px',
                      outline: 'none',
                      boxShadow: 'none',
                      border: 'none',
                      background: '#082341',
                      color: '#ffffff',
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = '#082341';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.outline = 'none';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {loading ? 'Processing...' : 'Proceed to Payment'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default UserInformationForm;
